/**
 * Thin fetch wrapper that talks to the API host (api./dev.api.ampersandbrands.com)
 * with cookie credentials. The host comes from `API_BASE` in `apiBase.ts`; every
 * caller builds its URL as `${API_BASE}/v1/...`. `credentials: 'include'` sends
 * the `.ampersandbrands.com` session cookie cross-origin.
 */

export type ApiError = Error & {
  status: number;
  body: unknown;
};

function makeError(status: number, body: unknown, message: string): ApiError {
  const err = new Error(message) as ApiError;
  err.status = status;
  err.body = body;
  return err;
}

async function readBody(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers ?? {});
  // Skip the JSON default for FormData bodies: the browser sets its own
  // multipart/form-data Content-Type with the boundary, which a hardcoded
  // application/json would break.
  if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  headers.set('Accept', 'application/json');

  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers,
  });

  if (res.status === 204) {
    return undefined as T;
  }

  // CloudFront's 403/404 error-response rule returns index.html with status 200
  // when the S3 OAC returns 403 (access denied) or when an API Gateway authorizer
  // denies the request. Detect HTML coming back on a JSON API call and treat it
  // as a 401 so AuthGate redirects to login rather than silently accepting the
  // HTML as valid session data and rendering a broken app state.
  const contentType = res.headers.get('content-type') ?? '';
  if (res.ok && contentType.includes('text/html')) {
    throw makeError(401, null, `API returned HTML on ${path} (session may have expired)`);
  }

  const body = await readBody(res);
  if (!res.ok) {
    throw makeError(res.status, body, `API ${res.status} on ${path}`);
  }
  return body as T;
}

export function apiGet<T>(path: string): Promise<T> {
  return apiFetch<T>(path, { method: 'GET' });
}

export function apiPost<T>(path: string, body: unknown): Promise<T> {
  return apiFetch<T>(path, { method: 'POST', body: JSON.stringify(body) });
}

// POST with a multipart/form-data body (file upload). Pass a pre-built
// FormData; apiFetch skips the JSON Content-Type for FormData so the browser
// sets its own boundary.
export function apiPostForm<T>(path: string, form: FormData): Promise<T> {
  return apiFetch<T>(path, { method: 'POST', body: form });
}

export function apiPut<T>(path: string, body: unknown): Promise<T> {
  return apiFetch<T>(path, { method: 'PUT', body: JSON.stringify(body) });
}

export function apiPatch<T>(path: string, body: unknown): Promise<T> {
  return apiFetch<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
}

export function apiDelete<T>(path: string): Promise<T> {
  return apiFetch<T>(path, { method: 'DELETE' });
}

export { API_BASE } from './apiBase';
